import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ApiError {
  status: number | null;
  message: string;
  serverMessage?: string;
  isRateLimited: boolean;
  isTimeout: boolean;
  isOffline: boolean;
  /** Suggested wait before retrying, in milliseconds, parsed from Retry-After header or body. */
  retryAfterMs?: number;
}

/**
 * Parse a Retry-After value (HTTP header or body field).
 * Supports delta-seconds (e.g. "120") and HTTP-date (e.g. "Wed, 21 Oct 2025 07:28:00 GMT").
 * Returns milliseconds, or undefined if unparseable / non-positive.
 */
export function parseRetryAfter(value: unknown): number | undefined {
  if (value == null) return undefined;
  const str = String(value).trim();
  if (!str) return undefined;

  // delta-seconds
  if (/^\d+(\.\d+)?$/.test(str)) {
    const secs = Number(str);
    if (Number.isFinite(secs) && secs >= 0) return Math.round(secs * 1000);
    return undefined;
  }

  // HTTP-date
  const dateMs = Date.parse(str);
  if (!Number.isNaN(dateMs)) {
    const diff = dateMs - Date.now();
    return diff > 0 ? diff : 0;
  }

  return undefined;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 8000,
};

/**
 * Extract structured error info from a Supabase edge-function error.
 */
export async function parseEdgeFunctionError(err: unknown): Promise<ApiError> {
  const raw = err as any;

  // Network/offline detection
  if (
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    return {
      status: null,
      message: "You appear to be offline. Please check your connection and try again.",
      isRateLimited: false,
      isTimeout: false,
      isOffline: true,
    };
  }

  // Timeout detection
  if (raw?.message === "timeout" || raw?.name === "AbortError") {
    return {
      status: null,
      message: "Request timed out. Please try again.",
      isRateLimited: false,
      isTimeout: true,
      isOffline: false,
    };
  }

  const status: number | undefined = raw?.context?.status ?? raw?.status;

  // Try to read server-provided message + Retry-After hint from body
  let serverMessage: string | undefined;
  let bodyRetryAfter: unknown;
  try {
    const ctx = raw?.context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.json();
      serverMessage = body?.error ?? body?.message;
      bodyRetryAfter =
        body?.retryAfter ?? body?.retry_after ?? body?.["Retry-After"];
    }
  } catch {
    /* ignore */
  }

  // Try to read Retry-After from response headers (Headers or plain object)
  let headerRetryAfter: unknown;
  try {
    const headers = raw?.context?.headers ?? raw?.response?.headers;
    if (headers) {
      if (typeof headers.get === "function") {
        headerRetryAfter = headers.get("retry-after") ?? headers.get("Retry-After");
      } else if (typeof headers === "object") {
        headerRetryAfter =
          headers["retry-after"] ?? headers["Retry-After"];
      }
    }
  } catch {
    /* ignore */
  }

  const retryAfterMs =
    parseRetryAfter(headerRetryAfter) ?? parseRetryAfter(bodyRetryAfter);

  if (status === 429) {
    return {
      status: 429,
      message:
        serverMessage ??
        "Too many requests. Please wait a moment and try again.",
      serverMessage,
      isRateLimited: true,
      isTimeout: false,
      isOffline: false,
      retryAfterMs,
    };
  }

  if (status === 503) {
    return {
      status: 503,
      message:
        serverMessage ??
        "Service temporarily unavailable. Please try again in a few moments.",
      serverMessage,
      isRateLimited: false,
      isTimeout: false,
      isOffline: false,
      retryAfterMs,
    };
  }

  if (status && status >= 400 && status < 500) {
    return {
      status,
      message:
        serverMessage ??
        raw?.message ??
        "We couldn't process your request. Please check your input and try again.",
      serverMessage,
      isRateLimited: false,
      isTimeout: false,
      isOffline: false,
      retryAfterMs,
    };
  }

  // 500+ or unknown
  return {
    status: status ?? null,
    message:
      serverMessage ??
      raw?.message ??
      "Something went wrong on our end. Please try again shortly.",
    serverMessage,
    isRateLimited: false,
    isTimeout: false,
    isOffline: false,
    retryAfterMs,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function nextDelay(attempt: number, base: number, max: number): number {
  const jitter = Math.random() * 0.3 + 0.85; // 0.85–1.15 jitter
  return Math.min(base * Math.pow(2, attempt), max) * jitter;
}

/**
 * Invoke a Supabase edge function with automatic retry on 429/5xx errors.
 * Returns the data directly; throws a descriptive error on final failure.
 */
export async function invokeWithRetry<T = any>(
  functionName: string,
  body: unknown,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const cfg = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= cfg.maxRetries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data as T;
    } catch (err) {
      const parsed = await parseEdgeFunctionError(err);
      lastError = parsed;

      // Don't retry client errors (4xx except 429)
      if (
        parsed.status &&
        parsed.status >= 400 &&
        parsed.status < 500 &&
        parsed.status !== 429
      ) {
        throw new Error(parsed.message);
      }

      // Don't retry if we're out of attempts
      if (attempt >= cfg.maxRetries) {
        break;
      }

      // Don't retry if offline
      if (parsed.isOffline) {
        throw new Error(parsed.message);
      }

      const delay = parsed.retryAfterMs != null
        ? Math.min(Math.max(parsed.retryAfterMs, 0), cfg.maxDelayMs)
        : nextDelay(attempt, cfg.baseDelayMs, cfg.maxDelayMs);
      await sleep(delay);
    }
  }

  throw new Error(
    lastError?.message ?? "Request failed after multiple attempts."
  );
}

/**
 * Convenience wrapper that invokes an edge function, handles retries,
 * and shows a toast on the final failure. Returns data or null.
 */
export async function invokeWithRetryAndToast<T = any>(
  functionName: string,
  body: unknown,
  toastTitle: string,
  config?: Partial<RetryConfig>
): Promise<T | null> {
  try {
    return await invokeWithRetry<T>(functionName, body, config);
  } catch (err: any) {
    toast.error(toastTitle, {
      description: err?.message ?? "Something went wrong. Please try again.",
    });
    return null;
  }
}
